import { useState, useEffect } from 'react';
import { getClients, createClient, deleteClient, reset2fa, updateClient, renameClient, getClientHealth, getClientScans, getClientSecurity, getClientEmails, getClientWatchlist, getThreatActors, assignThreatActor, unassignThreatActor, getTravelAdvisories, getTravelAdvisory, generateTravelAdvisory } from '../lib/api';

const TIERS = ['core', 'elite', 'black'];
const SECTORS = ['real estate', 'finance', 'technology', 'energy', 'government', 'healthcare', 'other'];
const BILLINGS = ['monthly', 'annual'];
const INDUSTRIES = ['real estate', 'finance', 'financial services', 'banking', 'technology', 'energy', 'oil & gas', 'petrochemical', 'government', 'defense', 'healthcare', 'telecommunications', 'retail', 'hospitality', 'media', 'academia', 'NGO', 'aerospace', 'other'];
const REGIONS = ['UAE', 'Saudi Arabia', 'GCC', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Middle East', 'Europe', 'North America', 'Asia Pacific', 'Global'];

const s = {
  page: { padding: 24, overflowY: 'auto', flex: 1 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 500 },
  addBtn: { background: 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', textAlign: 'left', padding: '8px 12px', borderBottom: '0.5px solid var(--border)' },
  td: { fontSize: 13, color: 'var(--text)', padding: '12px', borderBottom: '0.5px solid var(--border)', verticalAlign: 'middle' },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: 'var(--gold-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: 'var(--gold)', flexShrink: 0 },
  nameCell: { display: 'flex', alignItems: 'center', gap: 10 },
  tier: { fontSize: 10, padding: '2px 7px', borderRadius: 8, letterSpacing: '0.06em' },
  actionBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '0.5px solid var(--border)', background: 'var(--raised)', color: 'var(--text2)', cursor: 'pointer', marginLeft: 6 },
  dangerBtn: { fontSize: 11, padding: '4px 10px', borderRadius: 5, border: '0.5px solid var(--red)', background: 'var(--red-bg)', color: 'var(--red)', cursor: 'pointer', marginLeft: 6 },
  modal: { position: 'absolute', inset: 0, background: 'rgba(7,7,14,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, overflowY: 'auto', padding: '20px 0' },
  modalCard: { width: 520, background: 'var(--surface)', border: '0.5px solid var(--border2)', borderRadius: 12, padding: '28px 24px' },
  modalTitle: { fontSize: 16, fontWeight: 500, marginBottom: 20 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 },
  field: { marginBottom: 14 },
  label: { fontSize: 11, color: 'var(--text3)', letterSpacing: '0.06em', marginBottom: 5, display: 'block' },
  sectionLabel: { fontSize: 10, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, marginTop: 18, borderTop: '0.5px solid var(--border)', paddingTop: 14 },
  row: { display: 'flex', gap: 10, marginTop: 6 },
  cancelBtn: { flex: 1, background: 'var(--raised)', border: '0.5px solid var(--border)', color: 'var(--text2)', borderRadius: 7, padding: '9px', fontSize: 13, cursor: 'pointer' },
  saveBtn: { flex: 1, background: 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 7, padding: '9px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  qrBox: { background: 'var(--raised)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 16, textAlign: 'center', marginBottom: 14 },
  secret: { fontFamily: 'monospace', fontSize: 12, color: 'var(--text2)', wordBreak: 'break-all', marginTop: 8 },
  err: { fontSize: 12, color: 'var(--red)', background: 'var(--red-bg)', padding: '7px 10px', borderRadius: 6, marginBottom: 12 },
  ok: { fontSize: 12, color: 'var(--green)', background: 'var(--green-bg)', padding: '7px 10px', borderRadius: 6, marginBottom: 12 },
  empty: { textAlign: 'center', padding: '40px', color: 'var(--text2)', fontSize: 13 },
  scoreRing: { width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', border: '3px solid', flexShrink: 0 },
  profileGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  profileItem: { background: 'var(--raised)', borderRadius: 8, padding: '10px 14px' },
  profileItemLabel: { fontSize: 10, color: 'var(--text3)', letterSpacing: '0.07em', marginBottom: 4 },
  profileItemValue: { fontSize: 13, color: 'var(--text)', fontWeight: 500 },
};

const tierStyle = { core: { background: '#1a1a10', color: '#888060' }, elite: { background: '#101a1a', color: '#608080' }, black: { background: 'var(--gold-dim)', color: 'var(--gold)' } };
const gradeColor = { A: 'var(--green)', B: '#90b870', C: 'var(--amber)', D: 'var(--red)' };

function initials(name) { return (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return s?.slice(0,10) || '—'; } }

const PLATFORMS = ['LinkedIn', 'Twitter / X', 'Instagram', 'Facebook', 'TikTok', 'Snapchat', 'GitHub', 'Telegram', 'Reddit', 'Other'];
const PLATFORM_KEY_MAP = { linkedin: 'LinkedIn', twitter: 'Twitter / X', twitter_x: 'Twitter / X', x: 'Twitter / X', instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', snapchat: 'Snapchat', github: 'GitHub', telegram: 'Telegram', reddit: 'Reddit' };
function keyToPlatform(key) { return PLATFORM_KEY_MAP[key.toLowerCase()] || PLATFORMS.find(p => p.toLowerCase() === key.toLowerCase()) || 'Other'; }
const EMPTY_FORM = { id: '', name: '', role: '', job_title: '', company: '', sector: 'real estate', industry: 'other', region: 'GCC', tier: 'black', phone: '', email: '', emails_to_monitor: '', company_domain: '', social_usernames: [], billing: 'monthly', notes: '' };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [qrData, setQrData] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [renameId, setRenameId] = useState('');
  const [securityData, setSecurityData] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [watchlistData, setWatchlistData] = useState(null);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [allActors, setAllActors] = useState([]);
  const [watchlistAssignId, setWatchlistAssignId] = useState('');
  const [travelData, setTravelData] = useState(null);
  const [travelLoading, setTravelLoading] = useState(false);
  const [travelAdvisory, setTravelAdvisory] = useState(null);
  const [travelCountry, setTravelCountry] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [travelGenerating, setTravelGenerating] = useState(false);

  const load = async () => {
    try { const d = await getClients(); setClients(Object.values(d.clients || d)); }
    catch { setClients([]); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setMsg({ type: '', text: '' });
    setModal('add');
  };

  const openEdit = (c) => {
    setSelected(c);
    setForm({
      id: c.id, name: c.name || '', role: c.role || '', job_title: c.job_title || c.role || '',
      company: c.company || '',
      sector: c.sector || 'real estate', industry: c.industry || 'other', region: c.region || 'GCC',
      tier: c.tier || 'black', phone: c.phone || '',
      email: c.email || '',
      emails_to_monitor: (c.emails_to_monitor || []).join(', '),
      company_domain: c.company_domain || '',
      social_usernames: Object.entries(c.social_usernames || {}).map(([platform, username]) => ({ platform: keyToPlatform(platform), username: String(username).replace(/^@+/, '') })),
      billing: c.billing || 'monthly',
      notes: c.notes || '',
    });
    setMsg({ type: '', text: '' });
    setModal('edit');
  };

  const openProfile = async (c) => {
    setSelected(c);
    setModal('profile');
    setProfileData(null);
    setProfileLoading(true);
    try {
      const [health, scans] = await Promise.all([
        getClientHealth(c.id).catch(() => null),
        getClientScans(c.id).catch(() => null),
      ]);
      setProfileData({ health, scans: scans?.scans || [] });
    } catch { setProfileData({}); }
    finally { setProfileLoading(false); }
  };

  const openDelete = (c) => { setSelected(c); setModal('delete'); };

  const openSecurity = async (c) => {
    setSelected(c);
    setSecurityData(null);
    setModal('security');
    setSecurityLoading(true);
    try { setSecurityData(await getClientSecurity(c.id)); }
    catch { setSecurityData({}); }
    finally { setSecurityLoading(false); }
  };

  const openEmails = async (c) => {
    setSelected(c);
    setEmailData(null);
    setModal('emails');
    setEmailLoading(true);
    try { setEmailData(await getClientEmails(c.id)); }
    catch { setEmailData({ emails: [] }); }
    finally { setEmailLoading(false); }
  };

  const openWatchlist = async (c) => {
    setSelected(c);
    setWatchlistData(null);
    setWatchlistAssignId('');
    setModal('watchlist');
    setWatchlistLoading(true);
    try {
      const [wl, ta] = await Promise.all([
        getClientWatchlist(c.id).catch(() => ({ watchlist: [] })),
        getThreatActors().catch(() => ({ threat_actors: [] })),
      ]);
      setWatchlistData(wl.watchlist || []);
      setAllActors(ta.threat_actors || []);
    } catch { setWatchlistData([]); }
    finally { setWatchlistLoading(false); }
  };

  const handleWatchlistAssign = async () => {
    if (!watchlistAssignId || !selected) return;
    try {
      await assignThreatActor(parseInt(watchlistAssignId), selected.id, 'Manually assigned by admin');
      const wl = await getClientWatchlist(selected.id);
      setWatchlistData(wl.watchlist || []);
      setWatchlistAssignId('');
    } catch (e) { alert(e.message); }
  };

  const handleWatchlistRemove = async (actorId) => {
    if (!selected) return;
    try {
      await unassignThreatActor(actorId, selected.id);
      setWatchlistData(d => d.filter(a => a.id !== actorId));
    } catch (e) { alert(e.message); }
  };

  const openTravel = async (c) => {
    setSelected(c);
    setTravelData(null);
    setTravelAdvisory(null);
    setTravelCountry('');
    setTravelDate('');
    setModal('travel');
    setTravelLoading(true);
    try { setTravelData(await getTravelAdvisories(c.id)); }
    catch { setTravelData({ advisories: [] }); }
    finally { setTravelLoading(false); }
  };

  const handleGenerateTravelAdvisory = async () => {
    if (!travelCountry.trim() || !selected) return;
    setTravelGenerating(true);
    try {
      const adv = await generateTravelAdvisory(selected.id, travelCountry, travelDate || null);
      setTravelAdvisory(adv);
      const updated = await getTravelAdvisories(selected.id);
      setTravelData(updated);
      setTravelCountry('');
      setTravelDate('');
    } catch (e) { alert(e.message); }
    finally { setTravelGenerating(false); }
  };

  const loadTravelAdvisory = async (advisoryId) => {
    try {
      const adv = await getTravelAdvisory(selected.id, advisoryId);
      setTravelAdvisory({ ...adv, ...adv.advisory_content });
    } catch {}
  };

  const openRename = (c) => { setSelected(c); setRenameId(c.id); setMsg({ type: '', text: '' }); setModal('rename'); };

  const handleRename = async () => {
    if (!renameId.trim()) { setMsg({ type: 'err', text: 'New ID cannot be empty' }); return; }
    try {
      await renameClient(selected.id, renameId.trim());
      setModal(null);
      load();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
  };

  const buildClientPayload = () => {
    const social = {};
    (form.social_usernames || []).forEach(({ platform, username }) => {
      const key = platform.toLowerCase().replace(/\s*\/\s*x$/i, '').replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      if (key && username.trim()) social[key] = username.trim().replace(/^@+/, '');
    });
    return {
      ...form,
      job_title: form.job_title || form.role || '',
      industry: form.industry || 'other',
      region: form.region || 'GCC',
      emails_to_monitor: form.emails_to_monitor ? form.emails_to_monitor.split(',').map(s => s.trim()).filter(Boolean) : [],
      social_usernames: social,
    };
  };

  const handleCreate = async () => {
    if (!form.name || !form.id) { setMsg({ type: 'err', text: 'Client ID and name are required' }); return; }
    try {
      await createClient({ ...buildClientPayload(), onboarding_complete: true });
      const r = await reset2fa(form.id, form.name);
      setQrData(r); setModal('qr'); load();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
  };

  const handleEdit = async () => {
    try { await updateClient(selected.id, buildClientPayload()); setModal(null); load(); }
    catch (e) { setMsg({ type: 'err', text: e.message }); }
  };

  const handleDelete = async () => {
    try { await deleteClient(selected.id); setModal(null); load(); }
    catch (e) { setMsg({ type: 'err', text: e.message }); setModal(null); }
  };

  const handleReset2fa = async (c) => {
    try { const r = await reset2fa(c.id, c.name); setQrData(r); setModal('qr'); }
    catch (e) { alert(e.message); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const autoId = (name) => { if (!form.id) f('id', name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')); };

  const addSocial = () => f('social_usernames', [...(form.social_usernames || []), { platform: 'LinkedIn', username: '' }]);
  const removeSocial = (i) => f('social_usernames', form.social_usernames.filter((_, idx) => idx !== i));
  const updateSocial = (i, key, val) => f('social_usernames', form.social_usernames.map((item, idx) => idx === i ? { ...item, [key]: val } : item));

  return (
    <div style={{ ...s.page, position: 'relative' }}>
      <div style={s.header}>
        <div style={s.title}>Clients <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 400 }}>({clients.length})</span></div>
        <button style={s.addBtn} onClick={openAdd}>+ Add client</button>
      </div>

      {msg.text && <div style={msg.type === 'err' ? s.err : s.ok}>{msg.text}</div>}

      {loading ? <div style={s.empty}>Loading...</div> : clients.length === 0 ? <div style={s.empty}>No clients yet — add your first one</div> : (
        <table style={s.table}>
          <thead>
            <tr>{['Client', 'Role', 'Company', 'Tier', 'Sector', 'Actions'].map(h => <th key={h} style={s.th}>{h.toUpperCase()}</th>)}</tr>
          </thead>
          <tbody>
            {clients.map(c => (
              <tr key={c.id}>
                <td style={s.td}>
                  <div style={s.nameCell}>
                    <div style={s.avatar}>{initials(c.name)}</div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{c.id}</div>
                    </div>
                  </div>
                </td>
                <td style={{ ...s.td, color: 'var(--text2)' }}>{c.role || '—'}</td>
                <td style={{ ...s.td, color: 'var(--text2)' }}>{c.company || '—'}</td>
                <td style={s.td}><span style={{ ...s.tier, ...tierStyle[c.tier] }}>{(c.tier || 'core').toUpperCase()}</span></td>
                <td style={{ ...s.td, color: 'var(--text2)' }}>{c.sector || '—'}</td>
                <td style={s.td}>
                  <button style={s.actionBtn} onClick={() => openProfile(c)}>Profile</button>
                  <button style={{ ...s.actionBtn, ...(c.known_breaches > 0 ? { color: 'var(--red)', borderColor: 'var(--red)', background: 'var(--red-bg)' } : {}) }} onClick={() => openSecurity(c)}>
                    Security{c.known_breaches > 0 ? ` · ${c.known_breaches}` : ''}
                  </button>
                  <button style={s.actionBtn} onClick={() => openEdit(c)}>Edit</button>
                  <button style={s.actionBtn} onClick={() => openEmails(c)}>Emails</button>
                  <button style={s.actionBtn} onClick={() => openWatchlist(c)}>Threats</button>
                  <button style={s.actionBtn} onClick={() => openTravel(c)}>Travel</button>
                  <button style={s.actionBtn} onClick={() => openRename(c)}>Rename ID</button>
                  <button style={s.actionBtn} onClick={() => handleReset2fa(c)}>Reset 2FA</button>
                  <button style={s.dangerBtn} onClick={() => openDelete(c)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Profile modal */}
      {modal === 'profile' && selected && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, width: 560 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ ...s.avatar, width: 40, height: 40, fontSize: 14 }}>{initials(selected.name)}</div>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 16 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>{selected.role} · {selected.company}</div>
                </div>
              </div>
              <span style={{ ...s.tier, ...tierStyle[selected.tier], fontSize: 11 }}>{(selected.tier || 'core').toUpperCase()}</span>
            </div>

            {profileLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text2)', fontSize: 13 }}>Loading profile data...</div>
            ) : profileData ? (
              <>
                {/* Health score */}
                {profileData.health && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--raised)', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                    <div style={{ ...s.scoreRing, borderColor: gradeColor[profileData.health.grade] || 'var(--border)' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: gradeColor[profileData.health.grade] || 'var(--text)' }}>{profileData.health.grade}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{profileData.health.health_score}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Cyber Health — {profileData.health.status}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
                        {profileData.health.open_cases} open case{profileData.health.open_cases !== 1 ? 's' : ''} · {profileData.health.total_cases} total
                      </div>
                      {profileData.health.scan_risk_level && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                          Last scan risk: {profileData.health.scan_risk_level}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Profile details */}
                <div style={s.profileGrid}>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>CLIENT ID</div>
                    <div style={{ ...s.profileItemValue, fontFamily: 'monospace', fontSize: 11 }}>{selected.id}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>SECTOR</div>
                    <div style={s.profileItemValue}>{selected.sector || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>INDUSTRY</div>
                    <div style={s.profileItemValue}>{selected.industry || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>REGION</div>
                    <div style={s.profileItemValue}>{selected.region || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>JOB TITLE</div>
                    <div style={s.profileItemValue}>{selected.job_title || selected.role || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>EMAIL</div>
                    <div style={{ ...s.profileItemValue, fontSize: 12 }}>{selected.email || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>PHONE</div>
                    <div style={s.profileItemValue}>{selected.phone || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>DATE JOINED</div>
                    <div style={s.profileItemValue}>{fmtDate(selected.created || selected.created_at)}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>BILLING</div>
                    <div style={s.profileItemValue}>{selected.billing || '—'}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>LAST SCAN</div>
                    <div style={s.profileItemValue}>{fmtDate(profileData.health?.last_scan)}</div>
                  </div>
                  <div style={s.profileItem}>
                    <div style={s.profileItemLabel}>TOTAL SCANS</div>
                    <div style={s.profileItemValue}>{profileData.scans?.length ?? '—'}</div>
                  </div>
                </div>

                {/* Monitoring */}
                {(selected.emails_to_monitor?.length > 0 || selected.company_domain || Object.keys(selected.social_usernames || {}).length > 0) && (
                  <div style={{ background: 'var(--raised)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.07em', marginBottom: 8 }}>MONITORING</div>
                    {selected.emails_to_monitor?.length > 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                        <span style={{ color: 'var(--text3)' }}>Emails: </span>
                        {selected.emails_to_monitor.join(', ')}
                      </div>
                    )}
                    {selected.company_domain && (
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                        <span style={{ color: 'var(--text3)' }}>Domain: </span>{selected.company_domain}
                      </div>
                    )}
                    {Object.entries(selected.social_usernames || {}).map(([k, v]) => (
                      <div key={k} style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>
                        <span style={{ color: 'var(--text3)', textTransform: 'capitalize' }}>{k}: </span>@{String(v).replace(/^@+/, '')}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Close</button>
              <button style={s.saveBtn} onClick={() => { setModal(null); openEdit(selected); }}>Edit profile</button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={s.modalTitle}>{modal === 'add' ? 'Add new client' : `Edit — ${selected?.name}`}</div>
            {msg.text && <div style={msg.type === 'err' ? s.err : s.ok}>{msg.text}</div>}

            <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Identity</div>
            <div style={s.grid2}>
              <div><span style={s.label}>FULL NAME</span><input value={form.name} onChange={e => { f('name', e.target.value); if (modal === 'add') autoId(e.target.value); }} placeholder="Ahmed Al-Rashidi" /></div>
              <div><span style={s.label}>CLIENT ID</span><input value={form.id} onChange={e => f('id', e.target.value.toLowerCase().replace(/\s/g, '_'))} placeholder="ahmed_alrashidi" disabled={modal === 'edit'} /></div>
            </div>
            <div style={s.grid2}>
              <div><span style={s.label}>ROLE</span><input value={form.role} onChange={e => f('role', e.target.value)} placeholder="CEO" /></div>
              <div><span style={s.label}>EMAIL</span><input value={form.email} onChange={e => f('email', e.target.value)} placeholder="ahmed@company.com" type="email" /></div>
            </div>
            <div style={s.grid2}>
              <div><span style={s.label}>COMPANY</span><input value={form.company} onChange={e => f('company', e.target.value)} placeholder="Acme Corp" /></div>
              <div><span style={s.label}>PHONE / WHATSAPP</span><input value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="+971501234567" /></div>
            </div>
            <div style={s.grid3}>
              <div><span style={s.label}>SECTOR</span><select value={form.sector} onChange={e => f('sector', e.target.value)}>{SECTORS.map(x => <option key={x}>{x}</option>)}</select></div>
              <div><span style={s.label}>TIER</span><select value={form.tier} onChange={e => f('tier', e.target.value)}>{TIERS.map(x => <option key={x}>{x}</option>)}</select></div>
              <div><span style={s.label}>BILLING</span><select value={form.billing} onChange={e => f('billing', e.target.value)}>{BILLINGS.map(x => <option key={x}>{x}</option>)}</select></div>
            </div>
            <div style={s.grid3}>
              <div><span style={s.label}>INDUSTRY (for threat matching)</span><select value={form.industry} onChange={e => f('industry', e.target.value)}>{INDUSTRIES.map(x => <option key={x}>{x}</option>)}</select></div>
              <div><span style={s.label}>REGION</span><select value={form.region} onChange={e => f('region', e.target.value)}>{REGIONS.map(x => <option key={x}>{x}</option>)}</select></div>
              <div><span style={s.label}>JOB TITLE</span><input value={form.job_title} onChange={e => f('job_title', e.target.value)} placeholder="CEO" /></div>
            </div>

            <div style={s.sectionLabel}>Monitoring setup</div>
            <div style={s.field}>
              <span style={s.label}>EMAILS TO MONITOR (comma separated)</span>
              <input value={form.emails_to_monitor} onChange={e => f('emails_to_monitor', e.target.value)} placeholder="ahmed@work.com, ahmed@personal.com" />
            </div>
            <div style={s.grid2}>
              <div><span style={s.label}>COMPANY DOMAIN</span><input value={form.company_domain} onChange={e => f('company_domain', e.target.value)} placeholder="acmecorp.com" /></div>
              <div></div>
            </div>

            <div style={s.sectionLabel}>Social media usernames</div>
            {(form.social_usernames || []).map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <select value={item.platform} onChange={e => updateSocial(i, 'platform', e.target.value)} style={{ width: 150, flexShrink: 0 }}>
                  {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                </select>
                <input value={item.username} onChange={e => updateSocial(i, 'username', e.target.value)} placeholder="username (no @)" style={{ flex: 1 }} />
                <button type="button" onClick={() => removeSocial(i)} style={{ background: 'var(--red-bg)', color: 'var(--red)', border: '0.5px solid var(--red)', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addSocial} style={{ fontSize: 11, color: 'var(--gold)', background: 'none', border: '0.5px solid var(--gold)', borderRadius: 5, padding: '5px 12px', cursor: 'pointer', marginBottom: 4 }}>+ Add username</button>

            <div style={s.sectionLabel}>Notes</div>
            <div style={s.field}>
              <textarea value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Internal notes..." rows={2} style={{ resize: 'vertical', width: '100%' }} />
            </div>

            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={modal === 'add' ? handleCreate : handleEdit}>{modal === 'add' ? 'Create & generate QR' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'qr' && qrData && (
        <div style={s.modal}>
          <div style={s.modalCard}>
            <div style={s.modalTitle}>2FA setup — send to client</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>Share this QR code with the client. They scan it once with Microsoft Authenticator.</div>
            <div style={s.qrBox}>
              <img src={qrData.qr_url} alt="QR" style={{ width: 180, height: 180, border: '4px solid white', borderRadius: 6 }} />
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 10 }}>Or enter manually:</div>
              <div style={s.secret}>{qrData.secret}</div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text)' }}>Send client:</strong> their Client ID + this QR code URL:<br />
              <span style={{ fontSize: 11, color: 'var(--text3)', wordBreak: 'break-all' }}>{qrData.qr_url}</span>
            </div>
            <button style={s.saveBtn} onClick={() => setModal(null)}>Done</button>
          </div>
        </div>
      )}

      {modal === 'security' && selected && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, width: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{selected.name} — Security Intel</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{selected.id}</div>
              </div>
              {!securityLoading && securityData?.grade && (
                <div style={{ ...s.scoreRing, borderColor: gradeColor[securityData.grade], width: 52, height: 52 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: gradeColor[securityData.grade] }}>{securityData.grade}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>{securityData.health_score}</div>
                </div>
              )}
            </div>

            {securityLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text2)', fontSize: 13 }}>Loading security data...</div>
            ) : securityData && Object.keys(securityData).length > 0 ? (<>

              {/* Breach summary */}
              <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', marginBottom: 10 }}>BREACH DATABASE</div>
              {(securityData.breach_summary || []).map((bs, i) => (
                <div key={i} style={{ background: bs.count > 0 ? 'var(--red-bg)' : 'var(--raised)', border: `0.5px solid ${bs.count > 0 ? 'var(--red)' : 'var(--border)'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text2)' }}>{bs.email}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: bs.count > 0 ? 'var(--red)' : bs.checked ? 'var(--green)' : 'var(--amber)' }}>
                      {bs.count > 0 ? `${bs.count} breach${bs.count > 1 ? 'es' : ''}` : bs.checked ? 'Clean ✓' : 'Not checked yet'}
                    </div>
                  </div>
                  {bs.breach_names?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, lineHeight: 1.8 }}>
                      {bs.breach_names.map(n => <span key={n} style={{ display: 'inline-block', background: '#2a0a0a', border: '0.5px solid var(--red)', borderRadius: 4, padding: '1px 7px', marginRight: 5, marginBottom: 3 }}>{n}</span>)}
                    </div>
                  )}
                </div>
              ))}

              {/* Detailed breach data from full scan */}
              {(securityData.scan_breach_details || []).length > 0 && (<>
                <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', marginTop: 16, marginBottom: 10 }}>EXPOSED DATA (from last full scan)</div>
                {securityData.scan_breach_details.map((bd, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    {(bd.breaches || []).map((b, j) => (
                      <div key={j} style={{ background: 'var(--raised)', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{b.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{b.date}</span>
                        </div>
                        {b.data_exposed?.length > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 4 }}>
                            Exposed: {b.data_exposed.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </>)}

              {/* Impersonation */}
              {securityData.scan_impersonation?.suspicious_count > 0 && (<>
                <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', marginTop: 16, marginBottom: 10 }}>IMPERSONATION ACCOUNTS</div>
                {(securityData.scan_impersonation.suspicious_accounts || []).map((acc, i) => (
                  <div key={i} style={{ background: 'var(--raised)', border: '0.5px solid var(--amber)', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: 'var(--amber)' }}>{acc.platform} — @{acc.username}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{acc.flag}</div>
                  </div>
                ))}
              </>)}

              {/* Open cases */}
              {(securityData.cases || []).filter(c => c.status === 'OPEN').length > 0 && (<>
                <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.08em', marginTop: 16, marginBottom: 10 }}>OPEN INCIDENT CASES</div>
                {securityData.cases.filter(c => c.status === 'OPEN').map((c, i) => (
                  <div key={i} style={{ background: 'var(--raised)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>{c.incident_type || c.case_id}</span>
                      <span style={{ fontSize: 11, color: c.severity === 'CRITICAL' ? 'var(--red)' : 'var(--amber)' }}>{c.severity}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{c.summary || ''}</div>
                  </div>
                ))}
              </>)}

              {/* Scan timestamp */}
              {securityData.scan_timestamp && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 16 }}>
                  Last full scan: {fmtDate(securityData.scan_timestamp)}
                </div>
              )}
              {!securityData.scan_timestamp && (
                <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 16 }}>No full scan on record — breach names only (from daily monitoring)</div>
              )}

            </>) : (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text2)', fontSize: 13 }}>No security data found</div>
            )}

            {/* Password tools privacy notice */}
            <div style={{
              marginTop: 20, padding: '10px 14px',
              background: 'var(--raised)', border: '0.5px solid var(--border)',
              borderRadius: 8, fontSize: 11, color: 'var(--text3)', lineHeight: 1.6,
            }}>
              <strong style={{ color: 'var(--text2)' }}>Password tool results are not logged or accessible.</strong>
              {' '}This feature is privacy-preserving by design — password checks run entirely in the
              client's browser. No password data is ever transmitted to or stored by Shaheen servers.
            </div>

            <div style={{ ...s.row, marginTop: 14 }}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'emails' && selected && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, width: 580, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{selected.name} — Email history</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>{selected.id}</div>
            {emailLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text2)', fontSize: 13 }}>Loading...</div>
            ) : (emailData?.emails || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)', fontSize: 13 }}>No emails sent yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Type', 'Recipient', 'Subject', 'Status', 'Sent'].map(h => (
                      <th key={h} style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: '0.07em', textAlign: 'left', padding: '6px 10px', borderBottom: '0.5px solid var(--border)' }}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(emailData.emails || []).map((e, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: 11, padding: '9px 10px', borderBottom: '0.5px solid var(--border)', color: 'var(--gold)', whiteSpace: 'nowrap' }}>
                        {e.email_type.replace(/_/g, ' ')}
                      </td>
                      <td style={{ fontSize: 11, padding: '9px 10px', borderBottom: '0.5px solid var(--border)', color: 'var(--text2)', fontFamily: 'monospace' }}>
                        {e.recipient}
                      </td>
                      <td style={{ fontSize: 11, padding: '9px 10px', borderBottom: '0.5px solid var(--border)', color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.subject}
                      </td>
                      <td style={{ fontSize: 11, padding: '9px 10px', borderBottom: '0.5px solid var(--border)', color: e.status === 'sent' ? 'var(--green)' : 'var(--red)', fontWeight: 500 }}>
                        {e.status}
                      </td>
                      <td style={{ fontSize: 11, padding: '9px 10px', borderBottom: '0.5px solid var(--border)', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {e.sent_at ? new Date(e.sent_at.endsWith('Z') ? e.sent_at : e.sent_at + 'Z').toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ ...s.row, marginTop: 20 }}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'rename' && selected && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, width: 400 }}>
            <div style={s.modalTitle}>Rename login ID — {selected.name}</div>
            {msg.text && <div style={s.err}>{msg.text}</div>}
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.6 }}>
              This updates the login ID across all data: conversations, sessions, cases, scans, and 2FA.<br />
              The client will need to use the new ID to log in. Existing sessions stay active.
            </div>
            <div style={s.field}>
              <span style={s.label}>CURRENT ID</span>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text3)', padding: '8px 10px', background: 'var(--raised)', borderRadius: 6 }}>{selected.id}</div>
            </div>
            <div style={s.field}>
              <span style={s.label}>NEW LOGIN ID</span>
              <input
                value={renameId}
                onChange={e => setRenameId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="new_login_id"
                style={{ fontFamily: 'monospace' }}
                autoFocus
              />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Lowercase letters, numbers, and underscores only.</div>
            </div>
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleRename} disabled={renameId === selected.id || !renameId}>Save new ID</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'travel' && selected && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, width: 620, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{selected.name} — Travel Advisories ✈</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{selected.id}</div>
              </div>
            </div>

            {/* Generate advisory */}
            <div style={{ background: 'var(--raised)', borderRadius: 8, padding: '14px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.07em', marginBottom: 10 }}>GENERATE ADVISORY FOR CLIENT</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  style={{ flex: 1, fontSize: 12, padding: '7px 10px', background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text1)' }}
                  placeholder="Destination country (e.g. Russia, China)"
                  value={travelCountry}
                  onChange={e => setTravelCountry(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGenerateTravelAdvisory()}
                />
                <input
                  style={{ width: 140, fontSize: 12, padding: '7px 10px', background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text1)' }}
                  type="date"
                  value={travelDate}
                  onChange={e => setTravelDate(e.target.value)}
                />
                <button
                  onClick={handleGenerateTravelAdvisory}
                  disabled={!travelCountry.trim() || travelGenerating}
                  style={{ fontSize: 12, padding: '7px 16px', background: 'var(--gold)', color: '#0a0a0a', border: 'none', borderRadius: 6, cursor: travelCountry.trim() && !travelGenerating ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: travelCountry.trim() && !travelGenerating ? 1 : 0.5, whiteSpace: 'nowrap' }}
                >
                  {travelGenerating ? 'Generating…' : 'Generate'}
                </button>
              </div>
            </div>

            {/* Advisory result */}
            {travelAdvisory && (() => {
              const riskColors = { low: '#22c55e', medium: '#f59e0b', high: '#f97316', critical: '#ef4444' };
              const rc = riskColors[travelAdvisory.risk_level] || '#f59e0b';
              return (
                <div style={{ background: 'var(--raised)', borderRadius: 8, padding: '14px', marginBottom: 16, border: `0.5px solid ${rc}44` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 22 }}>{travelAdvisory.flag || '🌍'}</span>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text1)' }}>{travelAdvisory.country}</span>
                      {travelAdvisory.travel_date && <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>{travelAdvisory.travel_date}</span>}
                    </div>
                    <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 8, background: rc + '22', border: `0.5px solid ${rc}`, color: rc }}>
                      {(travelAdvisory.risk_level || 'MEDIUM').toUpperCase()} RISK
                    </span>
                  </div>
                  {travelAdvisory.ncsi_score && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>NCSI: {travelAdvisory.ncsi_score}/100{travelAdvisory.ncsi_rank ? ` · Rank #${travelAdvisory.ncsi_rank}` : ''}</div>}
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 10 }}>{travelAdvisory.risk_summary}</div>
                  {(travelAdvisory.recommendations || []).filter(r => r.priority === 'critical').slice(0, 3).map((r, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'var(--red)', display: 'flex', gap: 6, marginBottom: 4 }}>
                      <span>⚠</span><span>{r.text}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Past advisories list */}
            <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.07em', marginBottom: 10 }}>PAST ADVISORIES</div>
            {travelLoading ? (
              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text2)', fontSize: 13 }}>Loading…</div>
            ) : (travelData?.advisories || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text3)', fontSize: 13 }}>No travel advisories yet</div>
            ) : (
              <div>
                {(travelData.advisories || []).map(a => {
                  const riskColors2 = { low: '#22c55e', medium: '#f59e0b', high: '#f97316', critical: '#ef4444' };
                  const rc2 = riskColors2[a.risk_level] || '#f59e0b';
                  const flag = a.country_code?.length === 2
                    ? String.fromCodePoint(...[...a.country_code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65))
                    : '🌍';
                  return (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => loadTravelAdvisory(a.id)}>
                      <span style={{ fontSize: 18 }}>{flag}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{a.country}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {a.travel_date ? `Travel: ${a.travel_date} · ` : ''}
                          {new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: rc2 + '22', border: `0.5px solid ${rc2}`, color: rc2 }}>
                        {(a.risk_level || '').toUpperCase()}
                      </span>
                      {a.ncsi_score && <span style={{ fontSize: 10, color: 'var(--text3)', width: 50, textAlign: 'right' }}>NCSI {a.ncsi_score}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ ...s.row, marginTop: 16 }}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'watchlist' && selected && (
        <div style={s.modal} onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ ...s.modalCard, width: 580, maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>{selected.name} — Threat Watchlist</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{selected.id}</div>
              </div>
            </div>

            {watchlistLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--text2)', fontSize: 13 }}>Loading...</div>
            ) : (<>
              {(watchlistData || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text3)', fontSize: 13, marginBottom: 16 }}>No threat actors on watchlist</div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {(watchlistData || []).map(actor => {
                    const lc = { CRITICAL: '#EF4444', HIGH: '#F59E0B', MEDIUM: '#3B82F6', LOW: '#6B7280' }[actor.threat_level] || '#6B7280';
                    return (
                      <div key={actor.id} style={{ background: 'var(--raised)', border: `0.5px solid ${lc}44`, borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{actor.name}</span>
                            {actor.aliases?.length > 0 && <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>aka {actor.aliases.slice(0,2).join(', ')}</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, background: lc + '22', border: `0.5px solid ${lc}`, color: lc, fontWeight: 600 }}>{actor.threat_level}</span>
                            <button onClick={() => handleWatchlistRemove(actor.id)} style={{ fontSize: 10, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Remove</button>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {actor.origin_country && <span>🌍 {actor.origin_country} · </span>}
                          {actor.reason && <span>Reason: {actor.reason} · </span>}
                          <span>Added by {actor.added_by}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.07em', marginBottom: 8 }}>ADD TO WATCHLIST</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    style={{ flex: 1, fontSize: 12, padding: '7px 10px', background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text1)' }}
                    value={watchlistAssignId}
                    onChange={e => setWatchlistAssignId(e.target.value)}
                  >
                    <option value="">Select threat actor…</option>
                    {allActors.filter(a => !(watchlistData || []).find(w => w.id === a.id)).map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.threat_level})</option>
                    ))}
                  </select>
                  <button
                    onClick={handleWatchlistAssign}
                    disabled={!watchlistAssignId}
                    style={{ fontSize: 12, padding: '7px 16px', background: 'var(--gold)', color: '#0a0a0a', border: 'none', borderRadius: 6, cursor: watchlistAssignId ? 'pointer' : 'not-allowed', fontWeight: 600, opacity: watchlistAssignId ? 1 : 0.5 }}
                  >Add</button>
                </div>
              </div>
            </>)}

            <div style={{ ...s.row, marginTop: 16 }}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, width: 360 }}>
            <div style={s.modalTitle}>Remove client</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>Remove <strong style={{ color: 'var(--text)' }}>{selected?.name}</strong>? This will delete their profile and revoke portal access. This cannot be undone.</div>
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={{ ...s.saveBtn, background: 'var(--red)', color: 'white' }} onClick={handleDelete}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
